import { openDB } from 'idb';
import { DATA_VERSION } from './version';

async function getOrCreateDB(dbName, dbVersion) {
  const openDBUpsert = await openDB(dbName, dbVersion, {
    upgrade(db) {
      if (!db.objectStoreNames.contains('cache-keys')) {
        db.createObjectStore('cache-keys');
      }
      if (!db.objectStoreNames.contains('inbox')) {
        db.createObjectStore('inbox', { keyPath: 'id' });
      }
      if (!db.objectStoreNames.contains('label')) {
        db.createObjectStore('label', { keyPath: 'id' });
      }
      if (!db.objectStoreNames.contains('label_crm')) {
        db.createObjectStore('label_crm', { keyPath: 'id' });
      }
      if (!db.objectStoreNames.contains('team')) {
        db.createObjectStore('team', { keyPath: 'id' });
      }
    },
  });

  return openDBUpsert;
}

export class DataManager {
  constructor(accountId) {
    this.modelsToSync = ['inbox', 'label', 'label_crm', 'team'];
    this.accountId = accountId;
    this.db = null;
  }

  async initDb() {
    if (this.db) {
      return this.db;
    }

    const dbName = `cw-store-${this.accountId}`;
    const dbs = await window.indexedDB.databases();

    const currentDbs = dbs.filter(
      db => db.name === dbName && db.version !== +DATA_VERSION
    );

    if (currentDbs?.length > 0) {
      currentDbs.forEach(db => {
        // eslint-disable-next-line no-console
        window.indexedDB.deleteDatabase(db.name);
      });
      await new Promise(resolve => setTimeout(resolve, 5000));
    }

    this.db = await getOrCreateDB(dbName, +DATA_VERSION);

    return this.db;
  }

  validateModel(name) {
    if (!name) throw new Error('Model name is not defined');
    if (!this.modelsToSync.includes(name)) {
      throw new Error(`Model ${name} is not defined`);
    }
    return true;
  }

  async replace({ modelName, data }) {
    this.validateModel(modelName);

    this.db.clear(modelName);
    return this.push({ modelName, data });
  }

  async push({ modelName, data }) {
    this.validateModel(modelName);

    if (Array.isArray(data)) {
      const tx = this.db.transaction(modelName, 'readwrite');
      data.forEach(item => {
        tx.store.add(item);
      });
      await tx.done;
    } else {
      await this.db.add(modelName, data);
    }
  }

  async get({ modelName }) {
    this.validateModel(modelName);
    return this.db.getAll(modelName);
  }

  async setCacheKeys(cacheKeys) {
    Object.keys(cacheKeys).forEach(async modelName => {
      this.db.put('cache-keys', cacheKeys[modelName], modelName);
    });
  }

  async getCacheKey(modelName) {
    this.validateModel(modelName);

    return this.db.get('cache-keys', modelName);
  }

  async flushDb() {
    if (!this.db) {
      await this.initDb();
    }

    const storeNames = this.db.objectStoreNames;
    await Promise.all(
      Array.from(storeNames).map(storeName => this.db.clear(storeName))
    );

    await this.db.clear('cache-keys');

    const dbName = `cw-store-${this.accountId}`;
    await window.indexedDB.deleteDatabase(dbName);
    this.db = null;
    await this.initDb();
  }
}
